import React, { useRef, useState } from 'react'
import moment from 'moment'
import AsyncSelect from 'react-select/async'
import { Alert, Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import Loader from '../../../common/Loader'
import DownloadFormatDropdown from '../../../common/DownloadFormatDropdown'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import { fetchCondominiums } from '../../../../helpers/api/condominium'
import { exportRewardsReport, getRewardsAdminErrorMessage, getRewardsReport } from '../../../../helpers/api/rewardsAdmin'
import { Toast } from '../../../UI/toast/Toast'
import { REPORT_PRESETS, getPresetRange } from '../rewardsAdminUtils'
import ReportSummary from './ReportSummary'
import ReportEvolution from './ReportEvolution'

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 31, fontSize: '0.8333rem' }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  indicatorsContainer: (base) => ({ ...base, height: 29 }),
  menu: (base) => ({ ...base, zIndex: 5, fontSize: '0.8333rem' })
}

const loadCondominiums = async (search) => {
  try {
    const { data } = await fetchCondominiums(1, 20, { search })
    return (data || []).map((condominium) => ({ value: condominium.condominium_id, label: condominium.name }))
  } catch {
    return []
  }
}

const triggerDownload = (blob, filename) => {
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(href)
}

// HO-TASK-289: reporte financiero y de auditoría del programa (HO-TASK-277),
// con filtros por período y condominio, evolución mensual y exportación.
const RewardsReport = () => {
  const [preset, setPreset] = useState('this_month')
  const [range, setRange] = useState(() => getPresetRange('this_month'))
  const [condominium, setCondominium] = useState(null)
  const [exporting, setExporting] = useState(false)
  const lastReport = useRef(null)

  const invalidRange = !range.startDate || !range.endDate || range.endDate < range.startDate
  const filters = { ...range, condominiumId: condominium?.value || null }

  const { data, error, isLoading, mutate } = useSWRFetch(
    invalidRange ? null : ['rewards-report', filters.startDate, filters.endDate, filters.condominiumId],
    (key, startDate, endDate, condominiumId) => getRewardsReport({ startDate, endDate, condominiumId })
  )
  // Al cambiar un filtro se deja el reporte anterior atenuado mientras llega el nuevo.
  if (data?.data) lastReport.current = data.data
  const refreshing = isLoading && !!lastReport.current
  const report = data?.data || (refreshing ? lastReport.current : null)

  const selectPreset = (id) => {
    setPreset(id)
    if (id !== 'custom') setRange(getPresetRange(id))
  }

  const changeDate = (field, value) => {
    setPreset('custom')
    setRange((current) => ({ ...current, [field]: value }))
  }

  const download = async (format) => {
    setExporting(true)
    try {
      const blob = await exportRewardsReport({ ...filters, format })
      triggerDownload(blob, `homelan_rewards_${filters.startDate}_${filters.endDate}.${format}`)
    } catch (exportError) {
      Toast.fire({ type: 'error', title: getRewardsAdminErrorMessage(exportError, 'No pudimos descargar el reporte.') })
    } finally {
      setExporting(false)
    }
  }

  const renderContent = () => {
    if (invalidRange) {
      return <Alert color="warning">La fecha final no puede ser anterior a la inicial.</Alert>
    }
    if (isLoading && !refreshing) {
      return (
        <Card className="mb-3">
          <Loader padding="py-5" />
        </Card>
      )
    }
    if (error || !report) {
      return (
        <Alert color="danger">
          {getRewardsAdminErrorMessage(error, 'No pudimos cargar el reporte de Homelan Rewards.')}
          <Button color="link" size="sm" className="ml-1 p-0 align-baseline" onClick={() => mutate()}>
            Reintentar
          </Button>
        </Alert>
      )
    }
    return (
      <div style={{ opacity: refreshing ? 0.5 : 1, transition: 'opacity 150ms' }} aria-busy={refreshing}>
        <ReportSummary report={report} />
      </div>
    )
  }

  return (
    <>
      <Card className="mb-3">
        <CardBody className="pb-2">
          <Row form className="align-items-end">
            <Col sm={6} lg={3}>
              <FormGroup>
                <Label for="rewards-report-preset" className="fs--1">Período</Label>
                <Input
                  id="rewards-report-preset"
                  type="select"
                  bsSize="sm"
                  value={preset}
                  onChange={({ target }) => selectPreset(target.value)}
                >
                  {REPORT_PRESETS.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs={6} lg={2}>
              <FormGroup>
                <Label for="rewards-report-from" className="fs--1">Desde</Label>
                <Input
                  id="rewards-report-from"
                  type="date"
                  bsSize="sm"
                  value={range.startDate}
                  max={moment().format('YYYY-MM-DD')}
                  onChange={({ target }) => changeDate('startDate', target.value)}
                />
              </FormGroup>
            </Col>
            <Col xs={6} lg={2}>
              <FormGroup>
                <Label for="rewards-report-to" className="fs--1">Hasta</Label>
                <Input
                  id="rewards-report-to"
                  type="date"
                  bsSize="sm"
                  value={range.endDate}
                  onChange={({ target }) => changeDate('endDate', target.value)}
                />
              </FormGroup>
            </Col>
            <Col sm={8} lg={3}>
              <FormGroup>
                <Label for="rewards-report-condominium" className="fs--1">Condominio</Label>
                <AsyncSelect
                  inputId="rewards-report-condominium"
                  cacheOptions
                  defaultOptions
                  isClearable
                  loadOptions={loadCondominiums}
                  value={condominium}
                  onChange={setCondominium}
                  placeholder="Todos"
                  noOptionsMessage={() => 'Sin resultados'}
                  loadingMessage={() => 'Buscando...'}
                  styles={selectStyles}
                />
              </FormGroup>
            </Col>
            <Col sm={4} lg={2}>
              <FormGroup className="text-sm-right">
                <DownloadFormatDropdown
                  label={exporting ? 'Descargando...' : 'Descargar'}
                  onSelect={download}
                  disabled={exporting || invalidRange || !report}
                  color="falcon-default"
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>
      {renderContent()}
      {!invalidRange && <ReportEvolution endDate={range.endDate} condominiumId={filters.condominiumId} />}
    </>
  )
}

export default RewardsReport
